import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, Trash2, Edit2, Eye } from 'lucide-react';
import Button from './Button';
import Card from './Card';

import ImageViewer from './ImageViewer';

export interface Column<T> {
  header: string;
  accessor?: keyof T | ((item: T, ...args: any[]) => React.ReactNode);
  cell?: (item: T, openImageViewer: (imageUrl: string) => void) => React.ReactNode;
  sortable?: boolean;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  title?: string;
  isLoading?: boolean;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    onPageChange: (page: number) => void;
    onLimitChange?: (limit: number) => void;
  };
  actions?: {
    view?: (item: T) => void;
    edit?: (item: T) => void;
    delete?: (item: T) => void;
    viewTitle?: string;
    editTitle?: string;
    deleteTitle?: string;
    custom?: Array<{
      label: string;
      icon?: React.ReactNode;
      onClick: (event: React.MouseEvent, item: T) => void;
      color?: string;
      title?: string;
      hidden?: (item: T) => boolean;
    }>;
  };
  onRowClick?: (item: T) => void; // New prop for row click
  filters?: React.ReactNode;
  searchPlaceholder?: string;
  onSearch?: (term: string) => void;
  emptyMessage?: string;
  emptyIllustration?: React.ReactNode;
  emptyCtaButton?: React.ReactNode;
  sortBy?: keyof T | null;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: keyof T) => void;
}

function DataTable<T>({
  data = [],
  columns,
  keyField,
  title,
  isLoading = false,
  pagination,
  actions,
  onRowClick,
  filters,
  searchPlaceholder = "Rechercher...",
  onSearch,
  emptyMessage = "Aucune donnée disponible",
  emptyIllustration,
  emptyCtaButton,
  sortBy,
  sortOrder,
  onSort
}: DataTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);

  const openImageViewer = (imageUrl: string) => {
    setCurrentImage(imageUrl);
    setShowImageViewer(true);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, onSearch]);

  const handleSort = (field: keyof T) => {
    if (onSort) {
      onSort(field);
    }
  };

  const renderPagination = () => {
    if (!pagination) return null;

    const { page, limit, total, onPageChange, onLimitChange } = pagination;
    const totalPages = Math.ceil(total / limit);
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Affichage de {total > 0 ? startItem : 0} à {endItem} sur {total} éléments
        </div>
        <div className="flex items-center space-x-2">
          {onLimitChange && (
            <select
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm">
            Page {page} sur {totalPages || 1}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      {/* Header with search and filters */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
          
          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">
            {onSearch && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full md:w-64"
                />
              </div>
            )}
            
            {filters && (
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-gray-400" />
                {filters}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-12 flex flex-col items-center justify-center">
            {emptyIllustration && <div className="mb-4">{emptyIllustration}</div>}
            <p className="text-gray-500 mb-4">{emptyMessage}</p>
            {emptyCtaButton && <div>{emptyCtaButton}</div>}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      typeof column.accessor === 'string' && column.sortable
                        ? 'cursor-pointer hover:bg-gray-100'
                        : ''
                    }`}
                    onClick={() => {
                      if (typeof column.accessor === 'string' && column.sortable) {
                        handleSort(column.accessor);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      {column.header}
                      {typeof column.accessor === 'string' && column.sortable && sortBy === column.accessor && (
                        <span className="ml-1">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-bleu divide-y divide-gray-200">
              {data.map((item) => (
                <tr 
                  key={String(item[keyField])} 
                  className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick && onRowClick(item)}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      onRowClick(item);
                    }
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                >
                  {columns.map((column, colIndex) => (
                    <td key={colIndex} className="px-6 py-4 whitespace-nowrap">
                      {column.cell
                        ? column.cell(item, openImageViewer)
                        : typeof column.accessor === 'function'
                        ? column.accessor(item)
                        : column.accessor
                        ? String(item[column.accessor as keyof T] || '')
                        : null}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {actions.view && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); actions.view!(item); }}
                            title={actions.viewTitle}
                            aria-label={actions.viewTitle}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        {actions.edit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); actions.edit!(item); }}
                            title={actions.editTitle}
                            aria-label={actions.editTitle}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {actions.delete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); actions.delete!(item); }}
                            title={actions.deleteTitle}
                            aria-label={actions.deleteTitle}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                        {actions.custom && actions.custom.map((action, actionIndex) => (
                          <Button
                            key={actionIndex}
                            variant="ghost"
                            size="sm"
                            onClick={(e) => action.onClick(e, item)}
                            className={action.color}
                            title={action.title}
                            aria-label={action.title}
                          >
                            {action.icon}
                            {action.label && <span className="ml-1">{action.label}</span>}
                          </Button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {renderPagination()}

      {showImageViewer && currentImage && (
        <ImageViewer imageUrl={currentImage} onClose={() => setShowImageViewer(false)} />
      )}
    </Card>
  );
}

export default DataTable;